import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "#/components/ui/alert-dialog";
import { Button } from "#/components/ui/button";
import { Dialog, DialogTrigger } from "#/components/ui/dialog";
import { absSizeStr } from "#/features/trade/lib/trade-constants";
import { toNumber } from "#/lib/formatters";
import { PositionLimitCloseForm } from "./position-limit-close-form.tsx";
import { TradeDialogContent } from "./trade-dialog-content.tsx";
import type { PositionRowItem } from "./types/positions.types.ts";

type PositionCloseCellProps = {
	position: PositionRowItem;
	onCloseMarket: (coin: string, size: string, isLong: boolean) => void;
	onCloseLimitSubmit: (
		coin: string,
		size: string,
		isLong: boolean,
		price: string,
	) => void;
	onReverse: (coin: string, size: string, isLong: boolean) => void;
};

export function PositionCloseCell({
	position,
	onCloseMarket,
	onCloseLimitSubmit,
	onReverse,
}: PositionCloseCellProps) {
	const { t } = useTranslate();
	const [limitOpen, setLimitOpen] = useState(false);

	const szi = toNumber(position.szi);
	const isLong = szi >= 0;
	const absSize = absSizeStr(position.szi);
	const coin = position.coin;

	return (
		<div className="flex items-center gap-2">
			{/* Limit */}
			<Dialog open={limitOpen} onOpenChange={setLimitOpen}>
				<DialogTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="xs"
						className="h-auto px-0 py-0 text-trade-text-secondary transition-colors hover:bg-transparent hover:text-trade-text-strong data-[state=open]:text-[var(--trade-accent)]"
					>
						{t("order.type.limit")}
					</Button>
				</DialogTrigger>
				<TradeDialogContent
					title={`${t(isLong ? "positions.close-long" : "positions.close-short")} - ${coin}-USDC`}
				>
					<div className="mt-4">
						<PositionLimitCloseForm
							coin={coin}
							absSize={absSize}
							isLong={isLong}
							onSubmit={onCloseLimitSubmit}
							onClose={() => setLimitOpen(false)}
						/>
					</div>
				</TradeDialogContent>
			</Dialog>

			{/* Market */}
			<AlertDialog>
				<AlertDialogTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="xs"
						className="h-auto px-0 py-0 text-trade-text-secondary hover:bg-transparent hover:text-trade-text-strong"
					>
						{t("order.type.market")}
					</Button>
				</AlertDialogTrigger>
				<AlertDialogContent size="sm">
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("positions.close-market-title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("positions.close-market-description", {
								side: isLong ? "long" : "short",
								coin,
								size: absSize,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel size="sm">
							{t("general.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							size="sm"
							variant="destructive"
							onClick={() => void onCloseMarket(coin, absSize, isLong)}
						>
							{t("positions.close-position")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Reverse */}
			<AlertDialog>
				<AlertDialogTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="xs"
						className="h-auto px-0 py-0 text-trade-text-secondary hover:bg-transparent hover:text-trade-text-strong"
					>
						{t("positions.reverse")}
					</Button>
				</AlertDialogTrigger>
				<AlertDialogContent size="sm">
					<AlertDialogHeader>
						<AlertDialogTitle>{t("positions.reverse-title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("positions.reverse-description", {
								side: isLong ? "long" : "short",
								coin,
								newSide: isLong ? "short" : "long",
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel size="sm">
							{t("general.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							size="sm"
							variant="destructive"
							onClick={() => void onReverse(coin, absSize, isLong)}
						>
							{t("positions.reverse-position")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
